import { useState, useEffect, useRef } from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { roleLabel } from "@/lib/format";
import Logo from "@/components/Logo";
import { toast } from "sonner";
import {
  LayoutDashboard, ShoppingBag, Users, CreditCard, MessageSquareWarning,
  MapPin, Tags, UserCog, Truck, ChevronDown, LogOut, Menu, X,
  BellRing, FileBarChart2, Wallet, CalendarClock, Landmark, BookOpen, Coins, Trash2,
} from "lucide-react";
import { Button } from "@/components/ui/button";

const groups = (role) => {
  if (role === "logistics_manager") {
    return [
      {
        title: "Opérations",
        items: [
          { to: "/orders", label: "Commandes", icon: ShoppingBag },
          { to: "/couriers", label: "Livreurs", icon: Truck },
        ],
      },
    ];
  }
  return [
    { items: [{ to: "/dashboard", label: "Tableau de bord", icon: LayoutDashboard }] },
    {
      title: "Commercial",
      items: [
        { to: "/orders", label: "Commandes", icon: ShoppingBag },
        { to: "/clients", label: "Clients", icon: Users },
        { to: "/subscriptions", label: "Abonnements", icon: CreditCard },
        { to: "/complaints", label: "Réclamations", icon: MessageSquareWarning },
        { to: "/notifications", label: "Notifications", icon: BellRing },
        { to: "/reports", label: "Rapports", icon: FileBarChart2 },
      ],
    },
    {
      title: "Finances",
      items: [
        { to: "/expenses", label: "Dépenses", icon: Wallet },
        { to: "/tresorerie", label: "Trésorerie", icon: Coins },
        { to: "/comptabilite", label: "Comptabilité", icon: BookOpen },
        { to: "/fiscalite", label: "Fiscalité", icon: Landmark },
      ],
    },
    {
      title: "Réglages",
      items: [
        { to: "/geography", label: "Géographie", icon: MapPin },
        { to: "/tarifs", label: "Tarifs & Services", icon: Tags },
        { to: "/collecte", label: "Créneaux collecte", icon: CalendarClock },
        { to: "/personnel", label: "Personnel", icon: UserCog },
        { to: "/couriers", label: "Livreurs", icon: Truck },
        { to: "/maintenance", label: "Maintenance", icon: Trash2 },
      ],
    },
  ];
};

function Group({ group, onNav, badges }) {
  const [open, setOpen] = useState(true);
  if (!group.title) {
    return (
      <div className="space-y-1">
        {group.items.map((it) => (
          <NavItem key={it.to} {...it} onNav={onNav} badge={badges[it.to] || 0} />
        ))}
      </div>
    );
  }
  return (
    <div className="mt-4">
      <button
        onClick={() => setOpen(!open)}
        data-testid={`nav-group-${group.title}`}
        className="w-full flex items-center justify-between px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-slate-400 hover:text-slate-600"
      >
        {group.title}
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "" : "-rotate-90"}`} />
      </button>
      {open && (
        <div className="space-y-1 mt-1">
          {group.items.map((it) => (
            <NavItem key={it.to} {...it} onNav={onNav} badge={badges[it.to] || 0} />
          ))}
        </div>
      )}
    </div>
  );
}

function NavItem({ to, label, icon: Icon, onNav, badge }) {
  return (
    <NavLink
      to={to}
      onClick={onNav}
      data-testid={`nav-${to.replace("/", "")}`}
      className={({ isActive }) =>
        `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
          isActive ? "bg-brand-600 text-white shadow-sm" : "text-slate-600 hover:bg-slate-100"
        }`
      }
    >
      <Icon className="w-4.5 h-4.5" style={{ width: 18, height: 18 }} />
      <span className="flex-1">{label}</span>
      {badge > 0 && (
        <span
          data-testid="nav-notif-badge"
          className="ml-auto bg-amber-400 text-slate-900 text-[11px] font-bold rounded-full min-w-[20px] h-5 px-1.5 flex items-center justify-center"
        >
          {badge}
        </span>
      )}
    </NavLink>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [ordersCount, setOrdersCount] = useState(0);
  const sinceRef = useRef(null);
  const audioCtxRef = useRef(null);

  const playBeep = () => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      if (!audioCtxRef.current) audioCtxRef.current = new Ctx();
      const ctx = audioCtxRef.current;
      if (ctx.state === "suspended") ctx.resume();
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.connect(g); g.connect(ctx.destination);
      o.type = "sine"; o.frequency.value = 880;
      g.gain.setValueAtTime(0.0001, ctx.currentTime);
      g.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.35);
      o.start(); o.stop(ctx.currentTime + 0.36);
    } catch (e) { console.debug("Audio de notification indisponible", e); }
  };

  const fetchPending = () => {
    if (user.role !== "super_admin") return;
    api.get("/notifications?status=pending").then((r) => setPendingCount(r.data.pending || 0)).catch(() => {});
  };

  const EVENT_LABELS = {
    Nouveau: "Nouvelle commande",
    Collecté: "Commande collectée",
    Traité: "Commande prête",
    Dispatché: "En livraison",
    Livré: "Commande livrée",
  };

  const fetchActivity = () => {
    if (user.role !== "super_admin" && user.role !== "logistics_manager") return;
    const params = sinceRef.current ? `?since=${encodeURIComponent(sinceRef.current)}` : "";
    api.get(`/orders/meta/activity${params}`).then((r) => {
      setOrdersCount(r.data.counts?.all || 0);
      const events = r.data.events || [];
      if (sinceRef.current && events.length) {
        playBeep();
        events.slice(0, 5).forEach((e) => {
          toast.info(`${EVENT_LABELS[e.last_event] || "Mise à jour"} · ${e.order_number}`, {
            description: e.client_name || "",
          });
        });
      }
      sinceRef.current = r.data.now;
    }).catch(() => {});
  };

  useEffect(() => {
    fetchPending();
    fetchActivity();
    const id = setInterval(() => { fetchPending(); fetchActivity(); }, 15000);
    return () => clearInterval(id);
  }, []);
  useEffect(() => { fetchPending(); fetchActivity(); }, [location.pathname]);

  const badges = { "/notifications": pendingCount, "/orders": ordersCount };

  const doLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const sidebar = (
    <div className="flex flex-col h-full">
      <div className="px-5 py-5 border-b">
        <Logo size={32} />
      </div>
      <nav className="flex-1 overflow-y-auto px-3 py-4">
        {groups(user.role).map((g) => (
          <Group key={g.title || "home"} group={g} onNav={() => setMobileOpen(false)} badges={badges} />
        ))}
      </nav>
      <div className="p-3 border-t">
        <div className="flex items-center gap-3 px-2 py-2">
          <div className="w-9 h-9 rounded-full bg-brand-100 text-brand-700 flex items-center justify-center font-semibold text-sm">
            {user.name?.[0]?.toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold truncate">{user.name}</div>
            <div className="text-xs text-slate-500">{roleLabel[user.role]}</div>
          </div>
        </div>
        <Button
          data-testid="logout-btn"
          onClick={doLogout}
          variant="ghost"
          className="w-full justify-start text-slate-600 mt-1"
        >
          <LogOut className="w-4 h-4 mr-2" /> Déconnexion
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50 flex">
      <aside className="hidden lg:flex w-64 bg-white border-r fixed inset-y-0 left-0 z-30">{sidebar}</aside>

      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-40 flex">
          <div className="w-64 bg-white shadow-xl">{sidebar}</div>
          <div className="flex-1 bg-slate-900/50" onClick={() => setMobileOpen(false)} />
        </div>
      )}

      <div className="flex-1 lg:ml-64 flex flex-col min-w-0">
        <header className="lg:hidden sticky top-0 z-20 bg-white border-b flex items-center justify-between px-4 h-14">
          <Logo size={26} />
          <button data-testid="mobile-menu-btn" onClick={() => setMobileOpen(true)} className="p-2">
            <Menu className="w-6 h-6" />
          </button>
        </header>
        <main className="flex-1 p-4 sm:p-6 lg:p-8 max-w-[1400px] w-full mx-auto overflow-x-hidden">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
